import { Play } from 'lucide-react';
import type { TaskTemplate } from '../../types/tasks';
import { getAgentBySlug, getAgentColor } from '../../utils/agents';
import { useTaskStore } from '../../stores/taskStore';

const CADENCE_STYLES: Record<string, string> = {
  daily: 'bg-accent-purple/15 text-accent-purple-light',
  weekly: 'bg-info/15 text-info',
  biweekly: 'bg-avatar-cyan/15 text-avatar-cyan',
  monthly: 'bg-success/15 text-success',
  quarterly: 'bg-warning/15 text-warning',
  trigger: 'bg-error/15 text-error',
};

interface TemplateCardProps {
  template: TaskTemplate;
  onEdit: () => void;
}

export default function TemplateCard({ template, onEdit }: TemplateCardProps) {
  const instantiateTemplate = useTaskStore((s) => s.instantiateTemplate);
  const agent = template.assigned_agent ? getAgentBySlug(template.assigned_agent) : null;

  async function handleUse(e: React.MouseEvent) {
    e.stopPropagation();
    try {
      await instantiateTemplate(template.id);
    } catch (err) {
      console.error('Failed to instantiate template:', err);
    }
  }

  return (
    <div
      className="bg-bg-secondary border border-border-subtle rounded-lg p-4 hover:border-border-hover cursor-pointer transition-colors flex flex-col gap-3"
      onClick={onEdit}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onEdit();
        }
      }}
    >
      {/* Title + Cadence */}
      <div className="flex items-start justify-between gap-2">
        <h4 className="font-medium text-text-primary text-sm leading-snug line-clamp-2">
          {template.title}
        </h4>
        {template.cadence && (
          <span className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${CADENCE_STYLES[template.cadence] || 'bg-bg-tertiary text-text-muted'}`}>
            {template.cadence}
          </span>
        )}
      </div>

      {/* Description */}
      {template.description && (
        <p className="text-xs text-text-muted line-clamp-2">{template.description}</p>
      )}

      {/* Footer: Agent + Use button */}
      <div className="flex items-center justify-between mt-auto">
        {agent ? (
          <div className="flex items-center gap-1.5">
            <div className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-medium text-white ${getAgentColor(agent.department)}`}>
              {agent.name.charAt(0)}
            </div>
            <span className="text-xs text-text-secondary">{agent.name}</span>
          </div>
        ) : (
          <span className="text-xs text-text-muted">Unassigned</span>
        )}

        <button
          onClick={handleUse}
          className="flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium bg-accent-purple/15 text-accent-purple-light hover:bg-accent-purple/25 transition-colors"
          title="Create task from template"
        >
          <Play className="h-3 w-3" />
          Use
        </button>
      </div>
    </div>
  );
}
